// src/components/PlayerCompareAI.js
import React, { useMemo, useState } from 'react';
import './PlayerCompareAI.css';
import TeamBadge from './TeamBadge';
import { X, Trophy, TrendingUp, TrendingDown, Minus, Star, Zap, Shield, Activity } from 'lucide-react';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
  Tooltip as RTooltip,
} from 'recharts';

const PLAYER_COLORS = ['#818cf8', '#34d399', '#fb923c', '#f472b6'];

// Colori fissi per tipo di malus
const MALUS_COLORS = {
  'Amm.':      '#fbbf24',
  'Esp.':      '#ef4444',
  'Rig. sbag.':'#f97316',
  'Autogol':   '#8b5cf6',
  'Gol sub.':  '#64748b',
};
const MALUS_KEYS = ['Amm.', 'Esp.', 'Rig. sbag.', 'Autogol', 'Gol sub.'];

function v(x) { return Number(x || 0); }

function fmt(x, dec = 2) {
  const n = Number(x);
  return Number.isFinite(n) ? (dec === 0 ? String(Math.round(n)) : n.toFixed(dec)) : '—';
}

function roleOf(p) {
  return String(p.role || '').trim().toUpperCase();
}

function shortName(name) {
  const parts = String(name || '').trim().split(' ');
  return parts.length > 1 ? parts[parts.length - 1] : parts[0];
}

// ─── Pesi per ruolo ──────────────────────────────────────────────────────────
const ROLE_WEIGHTS = {
  P: { goals: 3, assists: 2, fmarkavg: 3, markavg: 2, caps: 0.3,
       ycards: -1, rcards: -3, penalties: 2, mpenalties: -1.5, owngoals: -4,
       gotgoals: -0.25, spenalties: 5,
       l5fmarkavg: 2.5, l5markavg: 2, l5goals: 1.5, l5assists: 1 },
  D: { goals: 5.5, assists: 3.5, fmarkavg: 3, markavg: 2, caps: 0.3,
       ycards: -1, rcards: -3, penalties: 2.5, mpenalties: -2, owngoals: -4,
       gotgoals: 0, spenalties: 0,
       l5fmarkavg: 2.5, l5markavg: 2, l5goals: 3, l5assists: 2 },
  C: { goals: 5.5, assists: 4, fmarkavg: 3, markavg: 2, caps: 0.3,
       ycards: -0.8, rcards: -2.5, penalties: 2.5, mpenalties: -2, owngoals: -4,
       gotgoals: 0, spenalties: 0,
       l5fmarkavg: 2.5, l5markavg: 2, l5goals: 3, l5assists: 2.5 },
  A: { goals: 9, assists: 3.5, fmarkavg: 3, markavg: 2, caps: 0.3,
       ycards: -0.5, rcards: -2, penalties: 4, mpenalties: -2.5, owngoals: -4,
       gotgoals: 0, spenalties: 0,
       l5fmarkavg: 2.5, l5markavg: 2, l5goals: 5, l5assists: 2 },
};

function scorePlayer(p) {
  const l5 = p.last5 || {};
  const role = roleOf(p);
  const w = ROLE_WEIGHTS[role] || ROLE_WEIGHTS['C'];
  const isGk = role === 'P';
  return (
    v(p.goals)      * w.goals     +
    v(p.assists)    * w.assists    +
    v(p.fmarkavg)   * w.fmarkavg   +
    v(p.markavg)    * w.markavg    +
    v(p.caps)       * w.caps       +
    v(p.ycards)     * w.ycards     +
    v(p.rcards)     * w.rcards     +
    v(p.penalties)  * w.penalties  +
    v(p.mpenalties) * w.mpenalties +
    v(p.owngoals)   * w.owngoals   +
    (isGk ? v(p.gotgoals) * w.gotgoals + v(p.spenalties) * w.spenalties : 0) +
    v(l5.fmarkavg)  * w.l5fmarkavg +
    v(l5.markavg)   * w.l5markavg  +
    v(l5.goals)     * w.l5goals    +
    v(l5.assists)   * w.l5assists
  );
}

function getCategoryRaw(p) {
  const l5 = p.last5 || {};
  const role = roleOf(p);
  const isGk = role === 'P';
  const goalW = role === 'A' ? 7 : role === 'D' ? 5 : role === 'P' ? 2 : 5;
  const attack = isGk
    ? v(p.spenalties) * 6 - v(p.gotgoals) * 0.5
    : v(p.goals) * goalW + v(p.assists) * 3.5 + v(p.penalties) * 2.5 - v(p.mpenalties) * 2;
  const quality     = v(p.fmarkavg) * 12 + v(p.markavg) * 8;
  const discipline  = Math.max(0, 30 - v(p.ycards) * 3 - v(p.rcards) * 10 - v(p.owngoals) * 5) + v(p.caps) * 0.5;
  const form        = v(l5.fmarkavg) * 12 + v(l5.markavg) * 8 + v(l5.goals) * goalW * 0.6 + v(l5.assists) * 3;
  return { attack, quality, discipline, form };
}

// ─── Righe tabella ───────────────────────────────────────────────────────────
const COMMON_ROWS = [
  { key: 'fmarkavg', label: 'Fanta voto med.', dec: 2, higherBetter: true  },
  { key: 'markavg',  label: 'Media voto',       dec: 2, higherBetter: true  },
  { key: 'caps',     label: 'Presenze',          dec: 0, higherBetter: true  },
  { key: 'ycards',   label: 'Ammonizioni',       dec: 0, higherBetter: false },
  { key: 'rcards',   label: 'Espulsioni',        dec: 0, higherBetter: false },
];

const OUTFIELD_ROWS = [
  { key: 'goals',      label: 'Gol',              dec: 0, higherBetter: true  },
  { key: 'assists',    label: 'Assist',           dec: 0, higherBetter: true  },
  { key: 'penalties',  label: 'Rig. segnati',     dec: 0, higherBetter: true  },
  { key: 'mpenalties', label: 'Rig. sbagliati',   dec: 0, higherBetter: false },
  { key: 'owngoals',   label: 'Autogol',          dec: 0, higherBetter: false },
];

const GK_ROWS = [
  { key: 'gotgoals',   label: 'Gol subiti',    dec: 0, higherBetter: false },
  { key: 'spenalties', label: 'Rigori parati', dec: 0, higherBetter: true  },
];

const LAST5_ROWS = [
  { key: 'fmarkavg', label: 'FV ultime 5',     dec: 2, higherBetter: true },
  { key: 'markavg',  label: 'MV ultime 5',     dec: 2, higherBetter: true },
  { key: 'goals',    label: 'Gol ultime 5',    dec: 0, higherBetter: true },
  { key: 'assists',  label: 'Assist ultime 5', dec: 0, higherBetter: true },
];

const GK_LAST5_ROWS = [
  { key: 'fmarkavg',  label: 'FV ultime 5',    dec: 2, higherBetter: true  },
  { key: 'markavg',   label: 'MV ultime 5',    dec: 2, higherBetter: true  },
  { key: 'gotgoals',  label: 'Gol sub. ult. 5',dec: 0, higherBetter: false },
  { key: 'spenalties',label: 'Rig. par. ult. 5',dec: 0, higherBetter: true  },
];

// ─── Tooltips ────────────────────────────────────────────────────────────────
function SharedTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="cai-radar-tip">
      <strong>{label ?? payload[0]?.payload?.stat}</strong>
      {payload.map(entry => (
        <div key={entry.name} style={{ color: entry.color ?? entry.fill }}>
          {entry.name}: {fmt(entry.value, entry.value % 1 === 0 ? 0 : 2)}
        </div>
      ))}
    </div>
  );
}

function MalusTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, e) => s + v(e.value), 0);
  return (
    <div className="cai-radar-tip">
      <strong>{label}</strong>
      {payload.filter(e => v(e.value) > 0).map(entry => (
        <div key={entry.name} style={{ color: entry.fill }}>
          {entry.name}: −{fmt(entry.value, 1)} pt
        </div>
      ))}
      <div style={{ marginTop: 4, fontWeight: 900, color: '#f87171' }}>
        Totale: −{fmt(total, 1)} pt
      </div>
    </div>
  );
}

// ─── Chip delta ───────────────────────────────────────────────────────────────
function DeltaChip({ value, higherBetter }) {
  if (!Number.isFinite(value) || value === 0)
    return <span className="cai-delta cai-delta--neutral"><Minus size={11} /></span>;
  const positive = higherBetter ? value > 0 : value < 0;
  return (
    <span className={`cai-delta ${positive ? 'cai-delta--pos' : 'cai-delta--neg'}`}>
      {positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
      {fmt(Math.abs(value), Math.abs(value) < 1 ? 2 : 0)}
    </span>
  );
}

// ─── Riga statistica ─────────────────────────────────────────────────────────
function StatRow({ row, list, colorMap, hoverId, setHoverId, last5 = false, gkOnly = false }) {
  const values = list.map(p => {
    const src = last5 ? (p.last5 || {}) : p;
    if (gkOnly) return roleOf(p) === 'P' ? v(src[row.key]) : null;
    return v(src[row.key]);
  });
  const numeric  = values.filter(x => x !== null);
  const maxVal   = numeric.length ? Math.max(...numeric) : 0;
  const minVal   = numeric.length ? Math.min(...numeric) : 0;
  const allEqual = maxVal === minVal;

  return (
    <tr className="cai-table__row">
      <td className="cai-table__label">{row.label}</td>
      {list.map((p, ci) => {
        const val       = values[ci];
        const displayNA = gkOnly && roleOf(p) !== 'P';
        const numVal    = val ?? 0;
        const isBest    = !allEqual && val !== null && (row.higherBetter ? numVal === maxVal : numVal === minVal);
        const isWorst   = !allEqual && val !== null && (row.higherBetter ? numVal === minVal : numVal === maxVal);
        const delta     = ci === 0 || displayNA || val === null ? null : numVal - (values[0] ?? 0);
        return (
          <td
            key={p.id}
            className={['cai-table__cell', isBest ? 'is-best' : '', isWorst ? 'is-worst' : '', hoverId === p.id ? 'is-hovered' : ''].filter(Boolean).join(' ')}
            style={{ '--p-color': colorMap[p.id] }}
            onMouseEnter={() => setHoverId(p.id)}
            onMouseLeave={() => setHoverId(null)}
          >
            <span className="cai-cell-val">{displayNA || val === null ? '—' : fmt(numVal, row.dec)}</span>
            {ci > 0 && delta !== null && <DeltaChip value={delta} higherBetter={row.higherBetter} />}
          </td>
        );
      })}
    </tr>
  );
}

// ─── Barra categoria ─────────────────────────────────────────────────────────
function CategoryBar({ label, icon, values, colorMap, hoverId, setHoverId }) {
  const max = Math.max(...values.map(x => x.val), 0.01);
  return (
    <div className="cai-cat-block">
      <div className="cai-cat-label">{icon}<span>{label}</span></div>
      <div className="cai-cat-bars">
        {values.map(({ p, val }) => {
          const pct   = Math.max(0, Math.min(100, (val / max) * 100));
          const isTop = val === max;
          return (
            <div
              key={p.id}
              className={`cai-cat-row ${hoverId === p.id ? 'is-hovered' : ''}`}
              onMouseEnter={() => setHoverId(p.id)}
              onMouseLeave={() => setHoverId(null)}
            >
              <span className="cai-cat-name">{shortName(p.name)}</span>
              <div className="cai-cat-track">
                <div className={`cai-cat-fill ${isTop ? 'is-top' : ''}`} style={{ width: `${pct}%`, background: colorMap[p.id] }} />
              </div>
              <span className="cai-cat-val" style={{ color: isTop ? colorMap[p.id] : undefined }}>{fmt(val, 1)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Verdict testuale ─────────────────────────────────────────────────────────
function buildVerdict(ranked) {
  const winner = ranked[0];
  const role   = roleOf(winner);
  const lbl    = { P: 'portiere', D: 'difensore', C: 'centrocampista', A: 'attaccante' }[role] || 'giocatore';
  const l5     = winner.last5 || {};
  const isClose = ranked.length > 1 && (winner.score - ranked[1].score) < winner.score * 0.08;

  let txt = `**${winner.name}** risulta il ${lbl} migliore del confronto`;
  if (isClose) txt += `, con un vantaggio minimo su **${ranked[1].name}**`;
  txt += '.';

  if (role === 'P') {
    if (v(winner.spenalties) > 0) txt += ` Ha parato ${v(winner.spenalties)} rigori, un contributo decisivo.`;
    if (v(winner.gotgoals) > 0)   txt += ` Gol subiti: ${v(winner.gotgoals)}.`;
  } else {
    const goals   = v(winner.goals);
    const assists = v(winner.assists);
    if (goals >= 8 && assists >= 5) txt += ` Eccelle sia in gol che in assist.`;
    else if (goals >= 8)  txt += ` Il suo contributo in gol è determinante.`;
    else if (assists >= 5) txt += ` Si distingue per la capacità di creare gioco.`;
  }

  if (v(l5.fmarkavg) >= 6.5) txt += ` Forma recente eccellente (FV ult. 5: ${fmt(v(l5.fmarkavg), 2)}).`;
  if (ranked.length > 1) txt += ` **${ranked[ranked.length - 1].name}** è il meno performante.`;
  return txt;
}

// ─── EXPORT DEFAULT ───────────────────────────────────────────────────────────
export default function PlayerCompareAI({ players, onClose }) {
  const list = (players || []).slice(0, 4);

  const allGk = list.length > 0 && list.every(p => roleOf(p) === 'P');
  const hasGk = list.some(p => roleOf(p) === 'P');

  const ranked = useMemo(() =>
    [...list].map((p, i) => ({ ...p, score: scorePlayer(p), colorIdx: i }))
             .sort((a, b) => b.score - a.score),
  [list]);

  const colorMap = useMemo(() => {
    const m = {};
    list.forEach((p, i) => { m[p.id] = PLAYER_COLORS[i % PLAYER_COLORS.length]; });
    return m;
  }, [list]);

  const [hoverId, setHoverId] = useState(null);
  const winner = ranked[0];

  // ── Radar ─────────────────────────────────────────────────────────────────
  const radarData = useMemo(() => {
    const metrics = allGk
      ? [
          { key: 'fmarkavg',   label: 'FV medio',    src: 'main'  },
          { key: 'markavg',    label: 'MV medio',    src: 'main'  },
          { key: 'gotgoals',   label: 'Gol sub.',    src: 'main', invert: true },
          { key: 'spenalties', label: 'Rig. parati', src: 'main'  },
          { key: 'caps',       label: 'Presenze',    src: 'main'  },
          { key: 'fmarkavg',   label: 'Forma FV',    src: 'last5' },
        ]
      : [
          { key: 'fmarkavg', label: 'FV medio',  src: 'main'  },
          { key: 'markavg',  label: 'MV medio',  src: 'main'  },
          { key: 'goals',    label: 'Gol',       src: 'main'  },
          { key: 'assists',  label: 'Assist',    src: 'main'  },
          { key: 'caps',     label: 'Presenze',  src: 'main'  },
          { key: 'fmarkavg', label: 'Forma FV',  src: 'last5' },
        ];
    return metrics.map(({ key, label, src, invert }) => {
      const vals = list.map(p => src === 'last5' ? v((p.last5 || {})[key]) : v(p[key]));
      const max  = Math.max(...vals, 0.01);
      const row  = { stat: label };
      list.forEach((p, i) => {
        const pct = Math.round((vals[i] / max) * 100);
        row[p.name] = invert ? 100 - pct : pct;
      });
      return row;
    });
  }, [list, allGk]);

  // ── Categorie ──────────────────────────────────────────────────────────────
  const categoryData = useMemo(() => {
    const cats = list.map(p => ({ p, raw: getCategoryRaw(p) }));
    return {
      attack:     cats.map(({ p, raw }) => ({ p, val: raw.attack })),
      quality:    cats.map(({ p, raw }) => ({ p, val: raw.quality })),
      discipline: cats.map(({ p, raw }) => ({ p, val: raw.discipline })),
      form:       cats.map(({ p, raw }) => ({ p, val: raw.form })),
    };
  }, [list]);

  // ── Bar chart metriche chiave ──────────────────────────────────────────────
  const barData = useMemo(() => {
    const metrics = allGk
      ? [
          { label: 'Gol sub.', key: 'gotgoals',   src: 'main' },
          { label: 'Rig. par.',key: 'spenalties',  src: 'main' },
          { label: 'FV med.',  key: 'fmarkavg',    src: 'main' },
          { label: 'Presenze', key: 'caps',        src: 'main' },
        ]
      : [
          { label: 'Gol',      key: 'goals',    src: 'main' },
          { label: 'Assist',   key: 'assists',  src: 'main' },
          { label: 'FV med.',  key: 'fmarkavg', src: 'main' },
          { label: 'Presenze', key: 'caps',     src: 'main' },
        ];
    return metrics.map(({ label, key, src }) => {
      const row = { label };
      list.forEach(p => { row[p.name] = src === 'last5' ? v((p.last5 || {})[key]) : v(p[key]); });
      return row;
    });
  }, [list, allGk]);

  // ── Malus chart ────────────────────────────────────────────────────────────
  const malusData = useMemo(() =>
    list.map(p => ({
      name:         shortName(p.name),
      fullName:     p.name,
      color:        colorMap[p.id],
      'Amm.':       +(v(p.ycards)     * 0.5).toFixed(1),
      'Esp.':       +(v(p.rcards)     * 1  ).toFixed(1),
      'Rig. sbag.': +(v(p.mpenalties) * 3  ).toFixed(1),
      'Autogol':    +(v(p.owngoals)   * 2  ).toFixed(1),
      'Gol sub.':   roleOf(p) === 'P' ? +(v(p.gotgoals) * 1).toFixed(1) : 0,
    })),
  [list, colorMap]);

  // Quali chiavi malus sono presenti (almeno un giocatore > 0)
  const activeMalusKeys = MALUS_KEYS.filter(k => malusData.some(d => d[k] > 0));

  const verdict = useMemo(() => buildVerdict(ranked), [ranked]);

  if (!list.length) return null;

  const tableRows    = allGk ? [...COMMON_ROWS, ...GK_ROWS] : [...COMMON_ROWS, ...OUTFIELD_ROWS];
  const last5Rows    = allGk ? GK_LAST5_ROWS : LAST5_ROWS;
  const attackLabel  = allGk ? 'Parate' : 'Attacco';

  return (
    <section className="cai-root glass">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="cai-header">
        <div className="cai-header__text">
          <h2>Confronto giocatori</h2>
          <p>{list.length} giocatori · punteggio pesato per ruolo e forma recente</p>
        </div>
        <button className="cai-close" onClick={onClose} aria-label="Chiudi"><X size={18} /></button>
      </div>

      {/* ── Podio ──────────────────────────────────────────────────── */}
      <div className="cai-podium">
        {ranked.map((p, idx) => {
          const color   = colorMap[p.id];
          const isFirst = idx === 0;
          const gap     = isFirst ? null : (winner.score - p.score).toFixed(1);
          const roleLbl = { P: 'Por', D: 'Dif', C: 'Cen', A: 'Att' }[roleOf(p)] || roleOf(p);
          return (
            <div
              key={p.id}
              className={`cai-podium-card ${isFirst ? 'is-winner' : ''} ${hoverId === p.id ? 'is-hovered' : ''}`}
              style={{ '--p-color': color }}
              onMouseEnter={() => setHoverId(p.id)}
              onMouseLeave={() => setHoverId(null)}
            >
              {isFirst && (
                <span className="cai-winner-crown" aria-hidden="true">
                  <Trophy size={13} />Migliore
                </span>
              )}
              <div className="cai-podium-card__rank">{idx + 1}</div>
              <TeamBadge team={p.team} size={44} />
              <div className="cai-podium-card__info">
                <strong>{p.name}</strong>
                <span>{p.team} · {roleLbl}</span>
              </div>
              <div className="cai-podium-card__score">
                <strong>{fmt(p.score, 1)}</strong>
                {gap && <span>−{gap}</span>}
              </div>
              <div className="cai-podium-card__bar">
                <div className="cai-podium-card__bar-fill" style={{ width: `${(p.score / winner.score) * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Categorie ──────────────────────────────────────────────── */}
      <div className="cai-categories">
        <CategoryBar label={attackLabel}  icon={<Star size={13}/>}     values={categoryData.attack}     colorMap={colorMap} hoverId={hoverId} setHoverId={setHoverId} />
        <CategoryBar label="Voti"         icon={<Zap size={13}/>}      values={categoryData.quality}    colorMap={colorMap} hoverId={hoverId} setHoverId={setHoverId} />
        <CategoryBar label="Disciplina"   icon={<Shield size={13}/>}   values={categoryData.discipline} colorMap={colorMap} hoverId={hoverId} setHoverId={setHoverId} />
        <CategoryBar label="Forma rec."   icon={<Activity size={13}/>} values={categoryData.form}       colorMap={colorMap} hoverId={hoverId} setHoverId={setHoverId} />
      </div>

      {/* ── Tabella + Radar ────────────────────────────────────────── */}
      <div className="cai-body">

        {/* Tabella */}
        <div className="cai-table-wrap">
          <table className="cai-table">
            <thead>
              <tr>
                <th className="cai-table__stat-head">Statistica</th>
                {list.map(p => (
                  <th
                    key={p.id}
                    className={`cai-table__player-head ${hoverId === p.id ? 'is-hovered' : ''}`}
                    style={{ '--p-color': colorMap[p.id] }}
                    onMouseEnter={() => setHoverId(p.id)}
                    onMouseLeave={() => setHoverId(null)}
                  >
                    <div className="cai-table__player-head-inner">
                      <TeamBadge team={p.team} size={22} />
                      <span className="cai-table__player-name">{p.name}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map(row => (
                <StatRow key={row.key} row={row} list={list} colorMap={colorMap} hoverId={hoverId} setHoverId={setHoverId} />
              ))}

              {/* Sezione GK in modalità mista */}
              {!allGk && hasGk && (
                <>
                  <tr className="cai-table__section-head">
                    <td colSpan={list.length + 1} className="cai-table__section-label">Statistiche portieri</td>
                  </tr>
                  {GK_ROWS.map(row => (
                    <StatRow key={row.key} row={row} list={list} colorMap={colorMap} hoverId={hoverId} setHoverId={setHoverId} gkOnly />
                  ))}
                </>
              )}

              <tr className="cai-table__section-head">
                <td colSpan={list.length + 1} className="cai-table__section-label">Ultime 5 partite</td>
              </tr>
              {last5Rows.map(row => (
                <StatRow key={`l5-${row.key}`} row={row} list={list} colorMap={colorMap} hoverId={hoverId} setHoverId={setHoverId} last5 />
              ))}
            </tbody>
          </table>
        </div>

        {/* Radar + bar chart */}
        <div className="cai-radar-wrap">
          <div className="cai-radar-head">
            <h3>Radar comparativo</h3>
            <p>Valori normalizzati 0–100{allGk ? ' · Gol sub. invertiti' : ''}</p>
          </div>
          <div className="cai-radar-chart">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="68%">
                <PolarGrid stroke="rgba(148,163,184,.15)" />
                <PolarAngleAxis dataKey="stat" tick={{ fill: 'rgba(226,232,240,.75)', fontSize: 11, fontWeight: 700 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                <RTooltip content={<SharedTooltip />} />
                {list.map(p => {
                  const isHov  = hoverId === p.id;
                  const dimmed = hoverId && !isHov;
                  return (
                    <Radar key={p.id} name={p.name} dataKey={p.name}
                      stroke={colorMap[p.id]} fill={colorMap[p.id]}
                      fillOpacity={dimmed ? 0.04 : isHov ? 0.35 : 0.18}
                      strokeWidth={isHov ? 2.5 : 1.5}
                      strokeOpacity={dimmed ? 0.25 : 1}
                    />
                  );
                })}
              </RadarChart>
            </ResponsiveContainer>
          </div>

          <div className="cai-radar-legend">
            {list.map(p => (
              <div key={p.id} className={`cai-legend-item ${hoverId === p.id ? 'is-active' : ''}`}
                style={{ '--p-color': colorMap[p.id] }}
                onMouseEnter={() => setHoverId(p.id)} onMouseLeave={() => setHoverId(null)}
              >
                <span className="cai-legend-dot" />
                <span className="cai-legend-name">{p.name}</span>
              </div>
            ))}
          </div>

          {/* Bar chart statistiche chiave */}
          <div className="cai-bar-head">
            <h3>Statistiche chiave</h3>
            <p>{allGk ? 'Gol sub. · Rig. par. · FV · Presenze' : 'Gol · Assist · FV medio · Presenze'}</p>
          </div>
          <div className="cai-bar-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} barGap={3} barCategoryGap="32%">
                <CartesianGrid stroke="rgba(148,163,184,.07)" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: 'rgba(226,232,240,.7)', fontSize: 10, fontWeight: 700 }} axisLine={false} tickLine={false} />
                <YAxis tick={false} axisLine={false} width={0} />
                <RTooltip content={<SharedTooltip />} cursor={{ fill: 'rgba(255,255,255,.04)' }} />
                {list.map(p => {
                  const isHov  = hoverId === p.id;
                  const dimmed = hoverId && !isHov;
                  return (
                    <Bar key={p.id} dataKey={p.name} fill={colorMap[p.id]} radius={[5, 5, 0, 0]} maxBarSize={24}
                      fillOpacity={dimmed ? 0.2 : isHov ? 1 : 0.75}
                      onMouseEnter={() => setHoverId(p.id)} onMouseLeave={() => setHoverId(null)}
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* ── Malus ── */}
          <div className="cai-malus-wrap">
            <div className="cai-malus-head">
              <div className="cai-malus-head__title">
                <h3>Malus</h3>
              </div>
              <p>Amm. −0.5 · Esp. −1 · Rig. sbag. −3 · Autogol −2{hasGk ? ' · Gol sub. −1' : ''}</p>
              <div className="cai-malus-legend">
                {activeMalusKeys.map(k => (
                  <span key={k} className="cai-malus-legend__item">
                    <span className="cai-malus-legend__dot" style={{ background: MALUS_COLORS[k] }} />
                    {k}
                  </span>
                ))}
              </div>
            </div>
            <div className="cai-malus-chart" style={{ '--malus-players': list.length }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={malusData} layout="vertical" barCategoryGap="28%" margin={{ left: 4, right: 40, top: 4, bottom: 4 }}>
                  <CartesianGrid stroke="rgba(148,163,184,.07)" horizontal={false} />
                  <XAxis type="number" tick={{ fill: 'rgba(226,232,240,.6)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={n => n > 0 ? `−${n}` : '0'} />
                  <YAxis type="category" dataKey="name" width={64} tick={{ fill: 'rgba(226,232,240,.8)', fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} />
                  <RTooltip content={<MalusTooltip />} cursor={{ fill: 'rgba(255,255,255,.03)' }} />
                  {activeMalusKeys.map((k, ki) => {
                    const isLast = ki === activeMalusKeys.length - 1;
                    return (
                      <Bar key={k} dataKey={k} stackId="malus" fill={MALUS_COLORS[k]}
                        radius={isLast ? [0, 4, 4, 0] : [0, 0, 0, 0]}
                      >
                        {isLast && (
                          <LabelList
                            position="right"
                            content={({ x, y, width, height, index }) => {
                              const d = malusData[index];
                              if (!d) return null;
                              const tot = activeMalusKeys.reduce((s, mk) => s + v(d[mk]), 0);
                              if (tot === 0) return null;
                              return (
                                <text x={x + width + 6} y={y + height / 2 + 1}
                                  fill="#f87171" fontSize={11} fontWeight={900}
                                  dominantBaseline="middle"
                                >
                                  −{fmt(tot, 1)}
                                </text>
                              );
                            }}
                          />
                        )}
                      </Bar>
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>{/* fine cai-radar-wrap */}
      </div>{/* fine cai-body */}

      {/* ── Verdict ────────────────────────────────────────────────── */}
      <div className="cai-verdict" style={{ '--winner-color': colorMap[winner.id] }}>
        <div className="cai-verdict__icon"><Trophy size={16} /></div>
        <div className="cai-verdict__text">
          {verdict.split(/(\*\*[^*]+\*\*)/).map((part, i) => {
            const match = part.match(/^\*\*(.+)\*\*$/);
            return match ? <strong key={i}>{match[1]}</strong> : <span key={i}>{part}</span>;
          })}
        </div>
      </div>

    </section>
  );
}
